import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Seo, breadcrumbLd } from "@/components/Seo";
import { PageHeader } from "@/components/PageHeader";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { CommunityCard } from "@/components/CommunityCard";
import { get, feeOrNA } from "@/lib/api";
import NotFound from "@/pages/NotFound";

export default function StatePage() {
  const { state } = useParams();
  const [filter, setFilter] = useState("");
  const { data, isError, isLoading } = useQuery({ queryKey: ["state", state], queryFn: () => get(`/states/${state}`) });
  if (isError) return <NotFound />;
  if (isLoading) return <div className="max-w-7xl mx-auto p-12 text-slate-600">Loading…</div>;
  const crumbs = [{ name: "Home", to: "/" }, { name: "States", to: "/states" }, { name: data.state, to: `/${state}` }];
  const cities = data.cities.filter((c) => c.city.toLowerCase().includes(filter.toLowerCase()));

  return (
    <div data-testid="state-page">
      <Seo title={`${data.state} HOA Directory - Fees, Rules & Restrictions`} path={`/${state}`}
        description={`Browse ${data.total} HOA communities across ${data.cities.length} cities in ${data.state}. Compare HOA fees, pet rules and rental restrictions.`}
        jsonLd={breadcrumbLd(crumbs)} />
      <PageHeader eyebrow={`${data.code} · ${data.total} communities`} title={`${data.state} HOAs`}
        subtitle={`Explore homeowners associations in ${data.cities.length} ${data.state} cities. Directory average monthly fee: ${feeOrNA(data.avg_fee)}.`}>
        <Breadcrumbs items={crumbs} />
      </PageHeader>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <h2 className="text-2xl sm:text-3xl font-semibold text-[#0B2150] mb-6">Featured in {data.state}</h2>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">{data.featured.map((c) => <CommunityCard key={c.id} c={c} />)}</div>
      </section>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <h2 className="text-2xl sm:text-3xl font-semibold text-[#0B2150]">Cities in {data.state}</h2>
          <label htmlFor="city-filter" className="sr-only">Filter cities</label>
          <input id="city-filter" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter cities…" data-testid="city-filter-input"
            className="rounded-full border border-[#CBD5E1] px-4 py-2 text-sm w-full sm:w-64" />
        </div>
        <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {cities.map((c) => (
            <li key={c.slug}>
              <Link to={`/${state}/${c.slug}`} data-testid={`city-link-${c.slug}`}
                className="flex items-center justify-between rounded-xl border border-[#E2E8F0] bg-white px-4 py-3 hover:border-[#03A896] transition-colors">
                <span className="text-sm font-medium text-[#0B2150]">{c.city}</span>
                <span className="text-xs text-slate-600">{c.count}{c.avg_fee != null && ` · $${c.avg_fee}`}</span>
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
